import React, { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { listAdmins, updateAdminStatus, deleteAdmin } from "../../services/admins"
import { getAdmin } from "../../services/helpers"
import {
  UsersIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/24/outline"

const AdminSystemUsers = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [query, setQuery] = useState("")
  const [processingId, setProcessingId] = useState(null)
  const currentAdmin = getAdmin()
  const isSuperAdmin = currentAdmin?.role === 'SUPER_ADMIN'
  const navigate = useNavigate()

  const fetchItems = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await listAdmins()
      setLoading(false)
      if (res.status === 200) {
        setItems(Array.isArray(res.data) ? res.data : [])
      } else {
        setError(res.error || 'Failed to load system users')
      }
    } catch (err) {
      setLoading(false)
      setError('An unexpected error occurred')
    }
  }

  useEffect(() => { fetchItems() }, [])

  const handleToggleStatus = async (userId, currentStatus) => {
    if (!isSuperAdmin) {
      alert('Only Super Admins can change user status')
      return
    }

    setProcessingId(userId)
    const newStatus = currentStatus === 1 ? 0 : 1
    try {
      const res = await updateAdminStatus(userId, newStatus)
      if (res.status === 200) {
        await fetchItems()
      } else {
        alert(res.error || 'Failed to update user status')
      }
    } catch (err) {
      alert('An unexpected error occurred')
    } finally {
      setProcessingId(null)
    }
  }

  const handleDelete = async (userId, userName) => {
    if (!isSuperAdmin) {
      alert('Only Super Admins can delete users')
      return
    }

    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return
    }

    setProcessingId(userId)
    try {
      const res = await deleteAdmin(userId)
      if (res.status === 200) {
        await fetchItems()
      } else {
        alert(res.error || 'Failed to delete user')
      }
    } catch (err) {
      alert('An unexpected error occurred')
    } finally {
      setProcessingId(null)
    }
  }

  const filtered = items.filter(user => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return (
      (user.name || '').toLowerCase().includes(q) ||
      (user.username || '').toLowerCase().includes(q) ||
      (user.phoneNumber || '').toString().includes(q) ||
      (user.role || '').toLowerCase().includes(q)
    )
  })

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-purple-100 text-purple-700 border-purple-200'
      case 'SUPER_MODERATOR':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'MODERATOR':
        return 'bg-cyan-100 text-cyan-700 border-cyan-200'
      case 'ADMIN':
        return 'bg-slate-100 text-slate-700 border-slate-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">System Users</h1>
          <p className="text-slate-500 mt-1">Manage administrators and staff permissions</p>
        </div>
        {isSuperAdmin && (
          <Link 
            to="/admin/system-users/new" 
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition shadow-lg shadow-slate-900/20"
          >
            <PlusIcon className="w-5 h-5" /> Add New User
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, username, phone, or role..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="mt-2 text-slate-500">Loading users...</p>
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
          {error}
          <button onClick={fetchItems} className="block mx-auto mt-2 text-sm font-bold hover:underline">Try Again</button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Username</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Phone</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Role</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 font-semibold text-slate-700">Created</th>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 font-semibold text-slate-700 text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 7 : 6} className="px-6 py-12 text-center text-slate-500">
                      No system users found.
                    </td>
                  </tr>
                ) : (
                  filtered.map(user => (
                    <tr key={user._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{user.name}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{user.username}</td>
                      <td className="px-6 py-4 text-slate-600">{user.phoneNumber}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 1 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {user.status === 1 ? (
                            <>
                              <CheckCircleIcon className="w-4 h-4" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="w-4 h-4" />
                              Inactive
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600 text-sm">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/system-users/${user._id}/edit`)}
                              className="p-2 text-cyan-600 hover:bg-cyan-50 rounded-lg transition"
                              title="Edit User"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user._id, user.status)}
                              disabled={processingId === user._id}
                              className={`p-2 rounded-lg transition ${
                                user.status === 1
                                  ? 'text-orange-600 hover:bg-orange-50'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={user.status === 1 ? 'Deactivate' : 'Activate'}
                            >
                              {processingId === user._id ? (
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                              ) : user.status === 1 ? (
                                <XCircleIcon className="w-4 h-4" />
                              ) : (
                                <CheckCircleIcon className="w-4 h-4" />
                              )}
                            </button>
                            {user.role !== 'SUPER_ADMIN' && (
                              <button
                                onClick={() => handleDelete(user._id, user.name)}
                                disabled={processingId === user._id}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                                title="Delete User"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSystemUsers
